import React, { useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { usePatternStudio } from '../context/PatternStudioContext';

const ACCENT_COLORS = [
  { value: '#C4B8A8', label: 'Warm Taupe' },
  { value: '#8B9E7E', label: 'Sage' },
  { value: '#E8C4B8', label: 'Blush' },
  { value: '#6B5744', label: 'Walnut' },
  { value: '#2C2C2C', label: 'Charcoal' },
  { value: '#D4A0C0', label: 'Lavender' },
];

const FONT_STYLES = [
  { value: 'minimal',   label: 'Minimal',   preview: 'Inter — clean & modern' },
  { value: 'nursery',   label: 'Nursery',   preview: 'Rounded — soft & friendly' },
  { value: 'botanical', label: 'Botanical', preview: 'Serif — elegant & natural' },
  { value: 'classic',   label: 'Classic',   preview: 'Playfair — timeless' },
];

function BrandingField({ label, value, onChange, placeholder, type = 'text', prefix }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">{label}</label>
      <div className={`flex items-center border border-gray-200 rounded-xl bg-white overflow-hidden focus-within:border-stone-400 focus-within:ring-2 focus-within:ring-stone-100 transition-all ${prefix ? '' : ''}`}>
        {prefix && (
          <span className="px-3 text-sm text-gray-400 border-r border-gray-200 bg-gray-50 py-2.5 flex-shrink-0">{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none bg-transparent"
        />
      </div>
    </div>
  );
}

export default function Step6Branding() {
  const { state, dispatch } = usePatternStudio();
  const branding = state.pattern.branding || {};
  const logoInputRef = useRef();

  function set(key, value) {
    dispatch({ type: 'SET_BRANDING', changes: { [key]: value } });
  }

  function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    set('logo_url', url);
  }

  return (
    <div className="p-6 max-w-xl">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Branding</h2>
      <p className="text-sm text-gray-500 mb-6">
        Add your shop branding so buyers know who made this pattern.
      </p>

      <div className="space-y-5">
        {/* Logo */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            Shop Logo
          </label>
          <div className="flex items-center gap-4">
            {branding.logo_url ? (
              <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
                <img src={branding.logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
                <button
                  onClick={() => set('logo_url', '')}
                  className="absolute top-0.5 right-0.5 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow"
                >
                  <X size={10} className="text-gray-600" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => logoInputRef.current?.click()}
                className="w-16 h-16 rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center cursor-pointer hover:border-stone-500 transition-all flex-shrink-0"
              >
                <Upload size={20} style={{ color: '#C4B8A8' }} />
              </div>
            )}
            <div className="flex-1">
              <button
                onClick={() => logoInputRef.current?.click()}
                className="px-4 py-2 rounded-xl border border-stone-300 text-sm text-gray-700 hover:bg-stone-50 transition-all"
              >
                {branding.logo_url ? 'Change Logo' : 'Upload Logo'}
              </button>
              <p className="text-xs text-gray-400 mt-1">PNG with transparent background works best</p>
            </div>
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          </div>
        </div>

        <BrandingField
          label="Shop Name"
          value={branding.shop_name || ''}
          onChange={v => set('shop_name', v)}
          placeholder="Your Etsy Shop Name"
        />

        <BrandingField
          label="Instagram"
          value={branding.instagram || ''}
          onChange={v => set('instagram', v)}
          placeholder="yourhandle"
          prefix="@"
        />

        <BrandingField
          label="Website"
          value={branding.website || ''}
          onChange={v => set('website', v)}
          placeholder="yourshop.etsy.com"
        />

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            Copyright Text
          </label>
          <input
            type="text"
            value={branding.copyright || `© ${new Date().getFullYear()} ${branding.shop_name || 'Your Shop'}. All rights reserved.`}
            onChange={e => set('copyright', e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-stone-400 focus:ring-2 focus:ring-stone-100 transition-all"
          />
        </div>

        {/* Accent color */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
            Accent Color
          </label>
          <div className="flex flex-wrap gap-3">
            {ACCENT_COLORS.map(c => (
              <button
                key={c.value}
                onClick={() => set('accent_color', c.value)}
                className="flex flex-col items-center gap-1.5"
                title={c.label}
              >
                <div
                  className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    background: c.value,
                    borderColor: branding.accent_color === c.value ? '#2C2C2C' : 'transparent',
                    boxShadow: branding.accent_color === c.value ? '0 0 0 2px white, 0 0 0 3px ' + c.value : 'none',
                  }}
                />
                <span className="text-xs text-gray-500">{c.label}</span>
              </button>
            ))}
            {/* Custom color */}
            <div className="flex flex-col items-center gap-1.5">
              <div className="relative w-8 h-8">
                <div
                  className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 overflow-hidden"
                  style={{ background: !ACCENT_COLORS.find(c => c.value === branding.accent_color) ? branding.accent_color || '#FAF7F4' : '#FAF7F4' }}
                />
                <input
                  type="color"
                  value={branding.accent_color || '#C4B8A8'}
                  onChange={e => set('accent_color', e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
              </div>
              <span className="text-xs text-gray-500">Custom</span>
            </div>
          </div>
        </div>

        {/* Font style */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
            Font Style
          </label>
          <div className="grid grid-cols-2 gap-2">
            {FONT_STYLES.map(f => (
              <button
                key={f.value}
                onClick={() => set('font_style', f.value)}
                className={`px-4 py-3 rounded-xl border text-left transition-all ${
                  branding.font_style === f.value
                    ? 'border-stone-400 bg-stone-50'
                    : 'border-gray-200 bg-white hover:border-stone-300'
                }`}
              >
                <div className="text-sm font-semibold text-gray-900">{f.label}</div>
                <div className="text-xs text-gray-400 mt-0.5">{f.preview}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Live branding preview */}
        {branding.shop_name && (
          <div className="mt-2 p-4 rounded-2xl border border-stone-200" style={{ background: '#FAF7F4' }}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Footer Preview</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {branding.logo_url && (
                  <img src={branding.logo_url} alt="" className="w-8 h-8 object-contain rounded" />
                )}
                <div>
                  <p className="text-sm font-semibold" style={{ color: branding.accent_color || '#6B5744' }}>
                    {branding.shop_name}
                  </p>
                  {branding.instagram && (
                    <p className="text-xs text-gray-400">@{branding.instagram}</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400 text-right">
                {branding.copyright || `© ${new Date().getFullYear()} ${branding.shop_name}`}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
